import { FC } from "react";

import styled from "@emotion/styled";
import { cmsImageUrl } from "src/lib/cmsImageUrl";
import translate from "src/lib/translate";
import { IconPlus } from "src/ui/assets/icons/IconPlus";
import Translate from "src/ui/components/Translate/Translate";

import IconLock from "./IconLock";
import MealAppDownloadBox from "./MealAppDownloadBox";

// Fanned meal photos at the bottom of the card. Decorative only — served through
// the CMS image proxy like the sibling meal cards (MealHeroBackground /
// RxDietCard). The source assets are 2752x1536 PNGs but we render them as ~148px
// squares, so we let the proxy crop, resize to ~2x the display box, and serve
// WebP — this drops each from multiple MB to tens of KB on slow connections.
const SIDE_PHOTO_PARAMS = {
  width: 300,
  height: 300,
  fit: "crop",
  crop: "center",
  autoFormat: true,
  quality: 75
} as const;
const CENTER_PHOTO_PARAMS = { ...SIDE_PHOTO_PARAMS, width: 260, height: 260 };

const mealPhotoLeft = cmsImageUrl(
  "7111a0a90f0018dcb96373ce3843ddb99192d63f-2752x1536.png",
  SIDE_PHOTO_PARAMS
);
const mealPhotoCenter = cmsImageUrl(
  "e863a1a2cac6e738d2119096919ac3cfc354b7de-2752x1536.png",
  CENTER_PHOTO_PARAMS
);
const mealPhotoRight = cmsImageUrl(
  "70a602fa4fadaa2011c5723123fe2a46bea12790-2752x1536.png",
  SIDE_PHOTO_PARAMS
);

// iOS Safari stops clipping to the border-radius wherever a descendant has a
// CSS filter (Photos' drop-shadow) overlapping the edge — the fanned photos
// hang past the bottom via negative offsets and show square corners instead
// of being clipped. The -webkit-mask-image forces the mask-based clip path,
// which fixes it.
const Card = styled.button`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3rem;
  width: 100%;
  margin: 0;
  padding: 5rem 1rem 0;
  border: none;
  overflow: hidden;
  cursor: default;
  background: var(--color-white, #fff);
  border-radius: 1.5rem;
  -webkit-mask-image: -webkit-radial-gradient(white, black);

  &[data-interactive="true"] {
    cursor: pointer;
  }

  &[data-variant="desktop"] {
    gap: 1rem;
    padding: 3rem 1.5rem 0;
  }
`;

// The wash is anchored to the top in pixels and resolves to the card's white by
// ~300px, filling the full card height. This keeps the fade-out independent of
// how tall the card is — on desktop the headline wraps to fewer lines, so a
// fixed-height gradient would leave its tail peeking out behind the photos.
const Background = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(
    180deg,
    #eee6ff 0,
    #dccbff 16px,
    #eee6ff 196px,
    var(--color-white, #fff) 300px
  );
`;

const Content = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  width: 100%;

  &[data-variant="desktop"] {
    gap: 1.5rem;
  }
`;

const Pill = styled.span`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.1875rem 0.5rem 0.0625rem;
  color: var(--Accessible-PurpleHaze, #5f1f8f);
  line-height: 0;

  svg {
    width: 0.75rem;
    height: 0.75rem;
  }
`;

const PillLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.33;
  letter-spacing: 0.48px;
  text-transform: uppercase;
`;

const TextGroup = styled.p`
  margin: 0;
  text-align: center;
  text-wrap: balance;
  color: var(--color-charcoal, #212121);
  font-family: "STK Bureau Serif", serif;
  font-weight: 300;

  &[data-variant="desktop"] {
    letter-spacing: -1px;
  }
`;

const Title = styled.span`
  display: block;
  font-size: 2.25rem;
  line-height: 2.5rem;
  letter-spacing: -0.54px;
`;

const Subtitle = styled.span`
  display: block;
  font-size: 1.625rem;
  line-height: 1.15;
  letter-spacing: -0.4px;
`;

const AddMeal = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  height: 2.75rem;
  padding: 0.375rem 1rem;
  border-radius: 200px;
  background: rgba(33, 33, 33, 0.04);
  color: var(--color-charcoal, #212121);
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.33;

  svg {
    width: 1rem;
    height: 1rem;
  }
`;

// The desktop card is wider, so the fan spreads out and the outer photos tilt
// less (±7.79° instead of ±10.67°) — matching the design rather than scaling the
// mobile arrangement up.
const Photos = styled.div`
  position: relative;
  width: 260px;
  height: 102px;
  filter: drop-shadow(-6px 20px 7.5px rgba(90, 68, 0, 0.32))
    drop-shadow(-53px 33px 18.5px rgba(90, 68, 0, 0.16))
    drop-shadow(-94px 59px 22px rgba(90, 68, 0, 0.05));

  &[data-variant="desktop"] {
    width: 358px;
    max-width: 100%;
    height: 140px;
  }
`;

const Photo = styled.img`
  position: absolute;
  object-fit: cover;
  border-radius: 1.5rem;
  pointer-events: none;
`;

const PhotoLeft = styled(Photo)`
  bottom: -48.69px;
  left: 0;
  width: 148.53px;
  height: 148.53px;
  transform: rotate(-10.67deg);

  [data-variant="desktop"] & {
    transform: rotate(-7.79deg);
  }
`;

const PhotoRight = styled(Photo)`
  bottom: -48.69px;
  right: -12px;
  width: 148.53px;
  height: 148.53px;
  transform: rotate(10.67deg);

  [data-variant="desktop"] & {
    right: 0;
    transform: rotate(7.79deg);
  }
`;

const PhotoCenter = styled(Photo)`
  bottom: -24.82px;
  left: 50%;
  width: 127.183px;
  height: 127.183px;
  transform: translateX(-50%);
`;

interface MealTodayEmptyCardProps {
  /**
   * Opens the meal photo picker. When omitted (e.g. on desktop, where meal
   * upload isn't offered), the card renders without the "Add a Meal" CTA and is
   * not interactive.
   */
  onAddMeal?: () => void;
  /**
   * Desktop swaps the upload CTA for an app-download box (QR + store links),
   * since meals can only be photographed in the app, and leads with the
   * one-line headline at the wider desktop metrics.
   */
  variant?: "mobile" | "desktop";
}

const MealTodayEmptyCard: FC<MealTodayEmptyCardProps> = ({
  onAddMeal,
  variant = "mobile"
}) => (
  <Card
    as={onAddMeal ? "button" : "div"}
    type={onAddMeal ? "button" : undefined}
    onClick={onAddMeal}
    data-interactive={onAddMeal ? "true" : "false"}
    data-variant={variant}
    aria-label={
      onAddMeal ? translate("mealInsights.today.empty.addMeal") : undefined
    }
  >
    <Background aria-hidden />
    <Content data-variant={variant}>
      <Pill>
        <IconLock />
        <PillLabel>
          <Translate msg="mealInsights.today.insightsLabel" />
        </PillLabel>
      </Pill>
      <TextGroup data-variant={variant}>
        {variant === "desktop" ? (
          <Title>
            <Translate msg="mealInsights.today.empty.subtitle" />
          </Title>
        ) : (
          <>
            <Title>
              <Translate msg="mealInsights.today.empty.title" />
            </Title>
            <Subtitle>
              <Translate msg="mealInsights.today.empty.subtitle" />
            </Subtitle>
          </>
        )}
      </TextGroup>
      {variant === "desktop" && (
        <MealAppDownloadBox msg="mealInsights.today.empty.downloadApp" />
      )}
      {onAddMeal && (
        <AddMeal>
          <IconPlus />
          <Translate msg="mealInsights.today.empty.addMeal" />
        </AddMeal>
      )}
    </Content>
    <Photos aria-hidden data-variant={variant}>
      <PhotoLeft src={mealPhotoLeft} alt="" />
      <PhotoRight src={mealPhotoRight} alt="" />
      <PhotoCenter src={mealPhotoCenter} alt="" />
    </Photos>
  </Card>
);

export default MealTodayEmptyCard;
